import React from 'react';
import { recordRenderCrash } from '../utils/telemetry.js';

/**
 * App-wide error boundary — prevents a render-time exception from white-screening the entire PWA.
 * React render errors are NOT caught by the global window.error listener, so this class component
 * is the only capture point for them. Shows a branded fallback with a reload action.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('App render error:', error, info && info.componentStack);
    // React render crashes aren't caught by the global window 'error' listener, so report them into
    // beta telemetry here (no-op for non-beta). Makes white-screen crashes visible in the wladmin
    // silent-signals instead of going unnoticed.
    try { recordRenderCrash(error, info && info.componentStack); } catch { /* never throw from the boundary */ }
  }

  handleReload = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'linear-gradient(135deg, #0F1C2E, #1a2f47)', color: '#fff',
          fontFamily: 'system-ui, -apple-system, sans-serif', padding: '24px', textAlign: 'center',
        }}>
          <div style={{ maxWidth: 440 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <h1 style={{ fontSize: 22, margin: '0 0 12px' }}>Something went wrong</h1>
            <p style={{ opacity: 0.85, lineHeight: 1.5, margin: '0 0 24px' }}>
              The app hit an unexpected error. Your data is safe — reloading usually fixes it.
            </p>
            <button onClick={this.handleReload} style={{
              background: 'linear-gradient(135deg, #C9A962, #E8D5A3)', color: '#0F1C2E',
              border: 'none', borderRadius: 8, padding: '12px 28px', fontSize: 15,
              fontWeight: 600, cursor: 'pointer',
            }}>
              Reload Healist
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
